import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import { HOMEPAGE_URL, SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { LOGO_URL } from 'consts/mediaUrls'

import SidebarItem from './SidebarItem'
import * as Styled from './styled'
import { useResponsive } from 'hooks/useResponsive'
import useSidebarData from './useSidebarData'

interface IPillRect {
  x: number
  y: number
  width: number
  height: number
}

const Sidebar: React.FC = () => {
  const sidebarData = useSidebarData()
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [pillRect, setPillRect] = useState<IPillRect | null>(null)

  const activeKey = sidebarData.find(item => item.isActive)?.key

  const measurePill = useCallback(() => {
    // The registered element is the TAB itself (SidebarItem's root via
    // `rootRef`), not its wrapper: on mobile the wrapper carries the horizontal
    // inset that keeps the pill clear of the 1px divider it draws.
    // The offsets are read in the pill's coordinate space, which holds as long
    // as `Styled.Sidebar` stays the tab's offsetParent — i.e. don't give
    // `SidebarItemWrapper` a `position` without revisiting this.
    const activeElement = activeKey ? itemRefs.current[activeKey] : null

    if (!activeElement) {
      setPillRect(null)
      return
    }

    setPillRect({
      x: activeElement.offsetLeft,
      y: activeElement.offsetTop,
      width: activeElement.offsetWidth,
      height: activeElement.offsetHeight
    })
  }, [activeKey])

  useLayoutEffect(measurePill, [measurePill, isMobile, sidebarData.length])

  useEffect(() => {
    window.addEventListener('resize', measurePill)

    return () => window.removeEventListener('resize', measurePill)
  }, [measurePill])

  return (
    <Styled.Sidebar>
      {!isMobile && (
        <Styled.SidebarLogoWrapper href={HOMEPAGE_URL}>
          <img src={LOGO_URL} alt="Logo" />
        </Styled.SidebarLogoWrapper>
      )}
      {pillRect && (
        <Styled.ActivePill
          style={{
            transform: `translate(${pillRect.x}px, ${pillRect.y}px)`,
            width: `${pillRect.width}px`,
            height: `${pillRect.height}px`
          }}
        />
      )}
      {sidebarData.map(item => (
        <Styled.SidebarItemWrapper
          key={item.key}
          onClick={() => item.onClick()}
          $isActive={item.isActive}
          data-test-id={item.tabTestId}
        >
          <SidebarItem
            rootRef={element => {
              itemRefs.current[item.key] = element
            }}
            icon={item.icon}
            label={item.label}
            isActive={item.isActive}
            isNewLabel={item.isNewLabel}
          />
        </Styled.SidebarItemWrapper>
      ))}
    </Styled.Sidebar>
  )
}

export default Sidebar
